import { associationRepo } from "@repositories";
import { HyperBackendMethod } from "@shared/backend/hyper-backend-method";
import { OrganizationRoles } from "@shared/Roles";
import dayjs from "dayjs";
import { Controller, dbNamesOf, repo, SqlDatabase } from "remult";
import { Association } from "../association";
import { AssociationTeam } from "../association-team";
import { Opportunity } from "../opportunity";
import { User } from "./entity";
import { phoneToIsraelSignedPhone } from "@shared/helpers/phone-to-israel-signed-phone";

export type UserRegistration = Record<
  "system" | "organization" | "team",
  {
    status: "registered" | "not-registered";
    isRegistered: boolean;
  }
>;

@Controller("user-methods")
export class UserMethods {
  static async checkUserRegistration(params: {
    id: string;
    organizationId?: string;
    teamId?: string;
  }): Promise<UserRegistration>;
  static async checkUserRegistration(params: {
    phoneNumber: string;
    organizationId?: string;
    teamId?: string;
  }): Promise<UserRegistration>;
  @HyperBackendMethod({
    allowed: true,
    apiPrefix: "user-methods",
    overrideSecurity: true,
  })
  static async checkUserRegistration(params: {
    phoneNumber?: string;
    id?: string;
    organizationId?: string;
    teamId?: string;
  }): Promise<UserRegistration> {
    const { phoneNumber, id, organizationId, teamId } = params;

    // Check if user exists in the system
    const user = id
      ? await repo(User).findId(id)
      : await repo(User).findOne({
          where: {
            phoneNumber: phoneToIsraelSignedPhone(phoneNumber!),
          },
        });

    // User not found at all
    if (!user || user.isInBlackList) {
      return genRet({
        system: false,
        organization: false,
        team: false,
      });
    }

    // No organizationId provided – only system-level check is relevant
    if (!organizationId) {
      return genRet({
        system: true,
        organization: false,
        team: false,
      });
    }

    const association = await associationRepo.findOne({
      where: Association.hyperFilter({
        userId: user.id,
        organizationId,
        roles: {
          $some: {
            $contains: OrganizationRoles.Volunteer,
          },
        },
      }),
    });

    // User not registered to the organization
    if (!association)
      return genRet({
        system: true,
        organization: false,
        team: false,
      });

    // No teamId provided – skip team check
    if (!teamId) {
      return genRet({
        system: true,
        organization: true,
        team: false,
      });
    }

    // Check if user is in the specific team within the organization
    const team = await repo(AssociationTeam).findOne({
      where: {
        teamId,
        associationId: association.id,
      },
    });

    // User not in the team
    if (!team) {
      return genRet({
        system: true,
        organization: true,
        team: false,
      });
    }

    // User is fully registered: system, organization, and team
    return genRet({
      system: true,
      organization: true,
      team: true,
    });

    // Helper to format the return object based on what level is registered
    function genRet(
      param: Record<
        keyof Awaited<ReturnType<typeof UserMethods.checkUserRegistration>>,
        boolean
      >,
    ): Awaited<ReturnType<typeof UserMethods.checkUserRegistration>> {
      const { system, organization, team } = param;

      return {
        system: {
          isRegistered: system,
          status: system ? "registered" : "not-registered",
        },
        organization: {
          isRegistered: organization,
          status: organization ? "registered" : "not-registered",
        },
        team: {
          isRegistered: team,
          status: team ? "registered" : "not-registered",
        },
      };
    }
  }

  static async findUsersWithOpportunities() {
    const CONFIG = {
      MAX_DISTANCE_KM: 10,
      MIN_OPPORTUNITIES_COUNT: 3,
    } as const;

    const oppDbNames = await dbNamesOf(Opportunity);

    const cmd = SqlDatabase.getDb().createCommand();
    const opp = await SqlDatabase.filterToRaw(
      Opportunity,
      {
        ...Opportunity.isInNeedOfParticipants({ spaces: 1 }),
        ...Opportunity.hyperFilter({
          status: "APPROVED",
          isClosed: false,
          startDate: { $gt: dayjs().add(3, "day").toDate() },
          endDate: { $lt: dayjs().add(3, "week").toDate() },
          isFull: false,
          $or: [
            {
              organization: {
                $is: { id: "d681d898-576f-446f-999f-90b8eaa5a2be" },
              },
            },
            { isPublic: true },
          ], // Example organization ID
        }),
      },
      undefined,
      oppDbNames,
    );

    const oppWhere = opp.replace(`"status"`, `"opportunity".status`); // Ensure correct table reference

    const userWhere = await SqlDatabase.filterToRaw(User, {});

    const sql = `
SELECT DISTINCT "userId"
FROM "association"
JOIN "user" ON "association"."userId" = "user".id
JOIN "opportunity" ON
  "user".coordinates_geog IS NOT NULL
  AND "opportunity".coordinates_geog IS NOT NULL
  AND
  ST_Distance("user".coordinates_geog, "opportunity".coordinates_geog) / 1000.0 <=
  CASE
    WHEN 
      ("user".availability_time::jsonb)->lower("opportunity".day) @> to_jsonb(ARRAY[
        CASE
          WHEN "opportunity".start_date::time BETWEEN '08:00:00' AND '12:00:00' THEN 'morning'
          WHEN "opportunity".start_date::time BETWEEN '12:00:00' AND '16:00:00' THEN 'afternoon'
          WHEN "opportunity".start_date::time BETWEEN '16:00:00' AND '20:00:00' THEN 'evening'
          ELSE NULL
        END
      ])
    THEN 12
    ELSE 15
  END
   ${`WHERE ${oppWhere} AND association."organizationId" = 'd681d898-576f-446f-999f-90b8eaa5a2be' AND ("opportunity".required_gender = "user".gender OR "opportunity".required_gender = '')`}
   GROUP BY "userId"
   HAVING COUNT(DISTINCT "opportunity".id) >= 3;
    `;

    const { rows } = await cmd.execute(sql);
    return rows;
  }

  @HyperBackendMethod({
    allowed: true,
    apiPrefix: "user-methods",
    overrideSecurity: true,
  })
  static async doesHaveAnyAssociationAsVolunteer(
    params: Record<"id", string> | Record<"phoneNumber", string>,
  ): Promise<boolean | "block"> {
    const associations = await associationRepo.find({
      where: Association.hyperFilter({
        user: {
          $is:
            "id" in params
              ? {
                  id: params.id,
                }
              : {
                  phoneNumber: phoneToIsraelSignedPhone(params.phoneNumber),
                },
        },
        roles: {
          $some: {
            $contains: OrganizationRoles.Volunteer,
          },
        },
      }),
      include: { user: true },
    });
    if (associations[0]?.user?.isInBlackList) {
      return "block";
    }
    return !!associations.length;
  }
}
